/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react'
import { useRouter } from 'next/router'
import { Grid, Checkbox, Box, Typography } from '@mui/material'
import { useFormik } from 'formik'
import * as yup from 'yup'
import Link from 'next/link'
import { useSnackbar } from 'notistack'
import RoundedTextField from 'src/components/shared/atoms/roundInputField_v3'
import PasswordInputField from 'src/components/shared/atoms/passwordInput'
import { ButtonComponent } from 'src/components/shared/atoms'
import TopicsPopup from 'src/components/shared/modules/topicPopup'
import {
  useSignupMutation,
  useCreateDtlUserMutation,
} from 'src/store/ApiCall/authApiCalls'
import useStyles from './Style'
import { getCookie, setCookie } from 'cookies-next'

const SignUpForm: React.FC = () => {
  const lang = getCookie('lang')

  const validationSchema = yup.object({
    username: yup
      .string()
      .max(
        32,
        lang === 'En'
          ? 'Username should be of maximum 16 characters'
          : 'El nombre de usuario debe tener un máximo de 16 caracteres.'
      )
      .required(
        lang === 'En' ? 'Username is required' : 'Se requiere nombre de usuario'
      ),
    email: yup
      .string()
      .email(
        lang === 'En'
          ? 'Enter a valid email'
          : 'Introduzca un correo electrónico válido'
      )
      .required(
        lang === 'En' ? 'Email is required' : 'correo electronico es requerido'
      ),
    age: yup
      .number()
      .required(lang === 'En' ? 'Age is required' : 'Se requiere edad')
      .positive(
        lang === 'En'
          ? 'Age should be positive number'
          : ' La edad debe ser un número positivo'
      )
      .integer(
        lang === 'En'
          ? 'Age should be a integer number'
          : 'La edad debe ser un número entero'
      ),
    password: yup
      .string()
      .min(
        8,
        lang === 'En'
          ? 'Password should be of minimum 8 characters'
          : 'La contraseña debe tener un mínimo de 8 caracteres'
      )
      .max(
        32,
        lang === 'En'
          ? 'Password should be of maximum 32 characters'
          : 'La contraseña debe tener un máximo de 32 caracteres'
      )
      .matches(
        /[a-z]/,
        lang === 'En'
          ? 'Password must contain at least one lowercase letter'
          : 'La contraseña debe contener al menos una letra minúscula'
      )
      .matches(
        /[A-Z]/,
        lang === 'En'
          ? 'Password must contain at least one uppercase letter'
          : 'La contraseña debe contener al menos una letra mayúscula'
      )
      .matches(
        /\d/,
        lang === 'En'
          ? 'Password must contain at least one number'
          : 'La contraseña debe contener al menos un número'
      )
      .matches(
        /[^A-Za-z0-9]/,
        lang === 'En'
          ? 'Password must contain at least one special character'
          : 'La contraseña debe contener al menos un carácter especial'
      )
      .required(
        lang === 'En' ? 'password is required' : 'se requiere contraseña'
      ),
    confirmPassword: yup
      .string()
      .oneOf(
        [yup.ref('password'), null],
        lang === 'En'
          ? 'Confirm Password should be same as Password'
          : 'Confirmar contraseña debe ser la misma que la contraseña'
      )
      .required(
        lang === 'En'
          ? 'Confirm password is required'
          : 'Se requiere confirmar contraseña'
      ),
    sendMeUpdates: yup.boolean(),
    tandc: yup
      .boolean()
      .oneOf(
        [true],
        lang === 'En'
          ? 'You must accept the terms and conditions'
          : 'Debe aceptar los Términos y Condiciones'
      ),
  })
  const router = useRouter()
  const { email } = router.query
  const [signup] = useSignupMutation()
  const [createDtlUser] = useCreateDtlUserMutation()
  const [showTopicPopup, setShowTopicPopup] = React.useState(false)
  const [userData, setUserData] = React.useState<{
    id: number
    username: string
  }>()
  const { enqueueSnackbar } = useSnackbar()

  const formik = useFormik({
    initialValues: {
      username: '',
      email: '',
      age: '',
      password: '',
      confirmPassword: '',
      sendMeUpdates: false,
      tandc: true,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const res: any = await signup({
        username: values.username,
        email: values.email,
        password: values.password,
        age: values.age,
        lang:
          typeof window === 'object' &&
          (window.location.href.includes('mx') ||
            window.location.href.includes('qle'))
            ? 'mx'
            : 'eng',
      })

      if (res) {
        const { data: strapiResData }: any = await createDtlUser({
          data: {
            username: values.username,
            email: values.email,
            password: values.password,
            invision_id: res.data.id.toString(),
          },
        })

        if (strapiResData.data) {
          localStorage.setItem(
            'dtl_token',
            JSON.stringify({
              id: strapiResData.data.id,
              username: strapiResData.data.attributes.username,
              avatar: '',
            })
          )
          setCookie(
            'dtl_token',
            JSON.stringify({
              id: strapiResData.data.id,
              username: strapiResData.data.attributes.username,
              avatar: '',
            })
          )
          enqueueSnackbar(
            lang === 'En' ? `user created` : 'creado por el usuario',
            {
              variant: 'success',
            }
          )
          setUserData({
            id: strapiResData?.data.id,
            username: strapiResData.data.attributes.username,
          })
          setShowTopicPopup(true)
        }
        // TODO: call api to store user info in strapi
      } else {
        // TODO: get appropriate status number and show the error
        enqueueSnackbar(
          lang === 'En'
            ? `request failed! please try again`
            : '¡solicitud fallida! Inténtalo de nuevo',
          {
            variant: 'error',
          }
        )
      }
    },
  })

  React.useEffect(() => {
    if (email) formik.setFieldValue('email', email)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email])

  const { classes } = useStyles()
  return (
    <>
      <Grid container className={classes.root}>
        <Box component="div" className={classes.headingContainer}>
          <Typography variant="h2" className={classes.title}>
            {lang === 'En' ? 'create your account' : 'Crea tu cuenta'}
          </Typography>
        </Box>
        <form onSubmit={formik.handleSubmit} className={classes.form}>
          <RoundedTextField
            aria-label="username"
            id="username"
            name="username"
            label={lang === 'En' ? 'Username' : 'Nombre de usuario'}
            placeHolder={lang === 'En' ? 'John Doe' : 'Juan Pérez'}
            value={formik.values.username}
            onChangeHandler={(e: React.ChangeEvent<any>) =>
              formik.handleChange(e)
            }
            error={formik.touched.username && Boolean(formik.errors.username)}
            helperText={formik.touched.username && formik.errors.username}
          />
          <RoundedTextField
            aria-label="email"
            id="email"
            name="email"
            label={lang === 'En' ? 'Email' : 'Correo electrónico'}
            type="email"
            placeHolder={lang === 'En' ? 'j.doe@email.com' : 'j.doe@email.com'}
            value={formik.values.email}
            onChangeHandler={(e: React.ChangeEvent<any>) =>
              formik.handleChange(e)
            }
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          <RoundedTextField
            aria-label="age"
            id="age"
            name="age"
            label={lang === 'En' ? 'Age' : 'Edad'}
            type="number"
            placeHolder={lang === 'En' ? '18' : 'dieciocho'}
            value={formik.values.age}
            onChangeHandler={(e: React.ChangeEvent<any>) =>
              formik.handleChange(e)
            }
            error={formik.touched.age && Boolean(formik.errors.age)}
            helperText={formik.touched.age && formik.errors.age}
          />

          <PasswordInputField
            aria-label="password"
            id="password"
            name="password"
            type="password"
            label={lang === 'En' ? 'Password' : 'Contraseña'}
            placeHolder={lang === 'En' ? '*******' : '*******'}
            value={formik.values.password}
            onChangeHandler={(e: React.ChangeEvent<any>) =>
              formik.handleChange(e)
            }
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />

          <PasswordInputField
            aria-label="confirm password"
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            label={lang === 'En' ? 'Confirm Password' : 'confirmar Contraseña'}
            placeHolder={lang === 'En' ? '*******' : '*******'}
            value={formik.values.confirmPassword}
            onChangeHandler={(e: React.ChangeEvent<any>) =>
              formik.handleChange(e)
            }
            error={
              formik.touched.confirmPassword &&
              Boolean(formik.errors.confirmPassword)
            }
            helperText={
              formik.touched.confirmPassword && formik.errors.confirmPassword
            }
          />

          <Box component="div" className={classes.checkboxContainer}>
            <Checkbox
              inputProps={{ 'aria-label': 'Send me news and updates' }}
              id="sendMeUpdates"
              name="sendMeUpdates"
              value={formik.values.sendMeUpdates}
              onChange={formik.handleChange}
            />
            <Typography variant="body2" className={classes.checkboxLabel}>
              {lang === 'En'
                ? 'Send me news and updates'
                : 'Envíame noticias y actualizaciones'}
            </Typography>
          </Box>
          <Box component="div" className={classes.checkboxContainer}>
            <Checkbox
              inputProps={{
                'aria-label': 'I agree to the Terms of Use and Privacy Policy',
              }}
              value={formik.values.tandc}
              onChange={formik.handleChange}
              defaultChecked
            />
            <Typography variant="body2" className={classes.checkboxLabel}>
              {lang === 'En' ? 'I agree to the' : 'Estoy de acuerdo con la'}{' '}
              <Link href="/" className={classes.tandcLinks}>
                {lang === 'En' ? 'Terms of Use' : 'Condiciones de uso'}
              </Link>{' '}
              {lang === 'En' ? 'and' : 'y'}{' '}
              <Link href="/" className={classes.tandcLinks}>
                {lang === 'En' ? 'Privacy Policy' : 'política de privacidad'}
              </Link>
            </Typography>
          </Box>
          <ButtonComponent
            type="text"
            text={lang === 'En' ? 'Create Account' : 'crea una cuenta'}
            className={classes.signUpBtn}
            gradient
            wrapperClass={classes.btnWrapper}
            isSubmitBtn
          />
        </form>
      </Grid>
      {userData && showTopicPopup ? (
        <TopicsPopup
          closeHandler={() => {
            setShowTopicPopup(false)
            router.push('/profile')
          }}
          userData={userData}
        />
      ) : null}
    </>
  )
}

export default SignUpForm
